const _excluded = ["showModal"];
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useState, createContext, useEffect, useMemo, useContext, useCallback } from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';
import { generateUniqueId } from "../../utils/generateUniqueId";
import { useIsMounted } from "../../utils/useIsMounted";
import { useStateInRef } from "../../utils/useStateInRef";
import { useBiContextGetter } from "../BiTracking/BiContext";
import { ConfirmationModal } from "../ConfirmationModal";
import { Loader } from "../Loader";
import { useTheme } from "../Theme";
import { Toast } from "../Toast";
import { ModalContainer } from "./ModalContainer";
import styles from "./OverlayContext.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const noop = () => {};
export const OverlayLoaderTID = 'async-backdrop';
const OverlayContext = /*#__PURE__*/createContext({
  isTestMode: false,
  hasOpenModals: false,
  getOpenedModals: () => [],
  showModal: async () => undefined,
  showConfirmAction: async () => Promise.resolve(),
  hideModal: noop,
  hideAllModals: noop,
  startLoader: noop,
  stopLoader: noop,
  showErrorToast: noop,
  showSuccessToast: noop,
  showInfoToast: noop
});
export function OverlayProvider(props) {
  const {
    children,
    ModalWrapper,
    isTestMode = false,
    disableEnforceFocus
  } = props;
  const isMounted = useIsMounted();
  const [modals, setModals] = useState([]);
  const openModals = modals.filter(m => m.isOpen);
  const getOpenModals = useStateInRef(openModals);
  const [loaders, setLoaders] = useState({});
  const [toasts, setToasts] = useState([]);
  const somethingIsLoading = Object.values(loaders).some(count => count > 0);
  const shouldBlurBackground = modals.some(modal => modal.shouldBlurBackground);
  const hasOpenModals = openModals.length > 0;
  useEffect(() => {
    return () => {
      setToasts(prevToasts => {
        prevToasts.forEach(toast => clearTimeout(toast.timer));
        return prevToasts;
      });
    };
  }, []);
  const staticMethods = useMemo(() => {
    function showToast(content) {
      const defaultDuration = 5;
      const id = "toast".concat(new Date().getTime());
      const transitionDuration = "".concat(defaultDuration * 0.95, "s");
      const timer = setTimeout(() => {
        isMounted() && setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
      }, defaultDuration * 1000);
      setToasts(prevToasts => prevToasts.concat({
        content,
        id,
        transitionDuration,
        timer
      }));
    }
    const showModal = params => {
      return new Promise(resolve => {
        setModals(prevModals => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const newModal = _objectSpread(_objectSpread({}, params), {}, {
            id: generateUniqueId('modal'),
            isOpen: true,
            resolve,
            dismissModal: () => {} // Is populated by the real function in the ModalContainer.
          });
          return prevModals.concat(newModal);
        });
      });
    };
    const showConfirmAction = async _ref => {
      let {
        testId,
        onConfirm,
        title,
        content: _content,
        primaryButtonLabel,
        secondaryButtonLabel,
        textAlignCenter = false,
        width,
        skin
      } = _ref;
      await showModal({
        closeOnClickOutside: true,
        closeOnEscapeKey: true,
        content: _ref2 => {
          let {
            dismissModal
          } = _ref2;
          return /*#__PURE__*/_jsx(ConfirmationModal, {
            testid: testId,
            skin: skin,
            title: title,
            content: _content,
            textAlignCenter: textAlignCenter,
            width: width,
            primaryButtonLabel: primaryButtonLabel,
            secondaryButtonLabel: secondaryButtonLabel,
            onPrimaryButtonClick: async () => {
              await (onConfirm === null || onConfirm === void 0 ? void 0 : onConfirm());
              dismissModal(true);
            },
            onSecondaryButtonClick: () => dismissModal(false)
          });
        }
      });
    };
    return {
      getOpenedModals() {
        return getOpenModals();
      },
      showModal,
      showConfirmAction,
      hideModal(value) {
        const openModals = getOpenModals();
        if (!openModals.length) return;
        openModals[openModals.length - 1].dismissModal(value);
      },
      hideAllModals(value) {
        for (const modal of getOpenModals()) {
          modal.dismissModal(value);
        }
      },
      startLoader: name => {
        const actualName = name || 'undefined';
        setLoaders(prevLoaders => _objectSpread(_objectSpread({}, prevLoaders), {}, {
          [actualName]: (prevLoaders[actualName] || 0) + 1
        }));
      },
      stopLoader: name => {
        const actualName = name || 'undefined';
        setLoaders(prevLoaders => _objectSpread(_objectSpread({}, prevLoaders), {}, {
          [actualName]: Math.max(0, (prevLoaders[actualName] || 0) - 1)
        }));
      },
      showErrorToast: (text, testid) => showToast( /*#__PURE__*/_jsx(Toast, {
        type: "error",
        text: text,
        testid: testid
      })),
      showSuccessToast: (text, testid) => showToast( /*#__PURE__*/_jsx(Toast, {
        type: "success",
        text: text,
        testid: testid
      })),
      showInfoToast: (text, testid) => showToast( /*#__PURE__*/_jsx(Toast, {
        type: "info",
        text: text,
        testid: testid
      }))
    };
  }, []);
  const contextValue = useMemo(() => _objectSpread(_objectSpread({}, staticMethods), {}, {
    isTestMode,
    hasOpenModals
  }), [isTestMode, hasOpenModals, staticMethods]);
  function onModalExited(id) {
    setModals(prevModals => prevModals.filter(m => m.id !== id));
  }
  function triggerClose(id) {
    setModals(prevModals => prevModals.map(m => m.id === id ? _objectSpread(_objectSpread({}, m), {}, {
      isOpen: false
    }) : m));
  }
  return /*#__PURE__*/_jsxs(OverlayContext.Provider, {
    value: contextValue,
    children: [/*#__PURE__*/_jsx("div", {
      className: cn(styles.overlayContext, {
        [styles.blur]: shouldBlurBackground
      }),
      children: children
    }), modals.map(modal => /*#__PURE__*/_jsx(ModalContainer, {
      modal: modal,
      ModalWrapper: ModalWrapper,
      isTestMode: isTestMode,
      disableEnforceFocus: disableEnforceFocus,
      triggerClose: triggerClose,
      onModalExited: onModalExited
    }, modal.id)), /*#__PURE__*/_jsx("div", {
      className: styles.toastsContainer,
      children: toasts.map(_ref3 => {
        let {
          id,
          content,
          transitionDuration
        } = _ref3;
        return /*#__PURE__*/_jsx("div", {
          className: styles.toast,
          style: {
            '--toastDuration': transitionDuration
          },
          children: content
        }, id);
      })
    }), somethingIsLoading && /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/_jsxs("div", {
      className: styles.globalLoader,
      "data-testid": "global-loader",
      children: [/*#__PURE__*/_jsx("div", {
        "data-testid": OverlayLoaderTID,
        className: styles.overlay
      }), /*#__PURE__*/_jsx(Loader, {})]
    }), document.body)]
  });
}
export function useOverlayContext() {
  const theme = useTheme();
  const _useContext = useContext(OverlayContext),
    {
      showModal
    } = _useContext,
    rest = _objectWithoutProperties(_useContext, _excluded);
  const getBiContext = useBiContextGetter();

  // useCallback ensures showModalWithBi is stable (same reference across renders)
  // getBiContext is already stable, so this callback is also stable
  const showModalWithBi = useCallback(config => {
    var _config$biCallerConte;
    return showModal(_objectSpread(_objectSpread({}, config), {}, {
      theme,
      biCallerContext: (_config$biCallerConte = config.biCallerContext) !== null && _config$biCallerConte !== void 0 ? _config$biCallerConte : getBiContext
    }));
  }, [showModal, theme, getBiContext]);
  return _objectSpread(_objectSpread({}, rest), {}, {
    showModal: showModalWithBi
  });
}